/**
 * KeyboardCheatSheetModal · Phase E v7 · 2026-02-28
 * ==================================================
 *
 * Global press-`?` shortcut overlay. Lists every power-user keyboard
 * shortcut currently active in the admin app so coaches don't have
 * to dig through the docs to discover the muscle-memory paths.
 *
 * Wiring
 * ------
 * Mounted once at the App level (next to the global Toaster). Listens
 * on `window keydown` for `?` (and `Escape` to close). Respects the
 * standard input-focus guardrails so typing `?` inside a search box
 * doesn't pop the modal.
 *
 * Why a separate component, not a hook?
 * -------------------------------------
 * The modal renders independently of every page-specific state tree.
 * Mounting it once means a single window listener, no race-conditions
 * between page mounts. Closing via the backdrop click or `Escape`
 * keeps focus management dead-simple — no inputs inside, nothing to
 * blur, no playback mutations underneath.
 */
import React, { useEffect, useState } from "react";
import { X } from "lucide-react";


const SHORTCUTS = [
  {
    section: "Master Player",
    items: [
      { keys: ["←", "→"], action: "Coarse scrub · ±1.0s" },
    ],
  },
  {
    section: "Pin Editor (Pass Editor)",
    items: [
      { keys: ["←", "→"], action: "Frame-perfect nudge · ±1 frame (≈33ms)" },
      { keys: ["Click"], action: "Set Release (R), then Catch (C) pin" },
    ],
  },
  {
    section: "Disc Annotation Strip",
    items: [
      { keys: ["Space"], action: "Burst-Annotate Empty (mark all remaining 30 frames as empty)" },
    ],
  },
  {
    section: "Global",
    items: [
      { keys: ["?"], action: "Open / Close this Cheat-Sheet" },
      { keys: ["Esc"], action: "Close any open modal" },
    ],
  },
];


export default function KeyboardCheatSheetModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      // Esc → close (universal modal contract). Run BEFORE the input
      // guard so users can dismiss the cheat-sheet from anywhere,
      // including while accidentally focusing an input.
      if (e.key === "Escape" && open) {
        setOpen(false);
        return;
      }
      // `?` → toggle. Input-focus guardrail: do NOT hijack the key
      // while the user is typing in a form field. We check both `?`
      // and Shift+/ since some keyboard layouts emit different codes.
      const ae = document.activeElement;
      const tag = ae?.tagName;
      const isText =
        tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT"
        || ae?.isContentEditable;
      if (isText) return;
      if (e.key === "?" || (e.key === "/" && e.shiftKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  return (
    <div
      data-testid="keyboard-cheatsheet-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={() => setOpen(false)}
    >
      <div
        className="relative max-w-2xl w-[90vw] bg-card border border-amber-300/40 rounded-md shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="keyboard-cheatsheet-close"
          onClick={() => setOpen(false)}
          className="absolute top-3 right-3 p-1 rounded-sm hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Close cheat-sheet"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="px-6 py-5 border-b border-border">
          <div className="font-mono text-[11px] uppercase tracking-widest text-amber-200">
            Keyboard Cheat-Sheet
          </div>
          <div className="text-2xl font-bold mt-1">Power-Coach Shortcuts</div>
          <div className="text-xs text-muted-foreground mt-1">
            Press <kbd className="px-1.5 py-0.5 border border-border rounded-sm font-mono text-[10px]">?</kbd> anywhere to toggle this menu.
          </div>
        </div>
        <div className="px-6 py-5 grid sm:grid-cols-2 gap-x-8 gap-y-5 max-h-[70vh] overflow-y-auto">
          {SHORTCUTS.map((sec) => (
            <div key={sec.section}>
              <div className="font-mono text-[10px] uppercase tracking-widest text-amber-200/80 mb-2">
                {sec.section}
              </div>
              <ul className="space-y-2">
                {sec.items.map((it, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="flex gap-1 shrink-0 mt-0.5">
                      {it.keys.map((k) => (
                        <kbd
                          key={k}
                          className="px-1.5 py-0.5 border border-border bg-background rounded-sm font-mono text-[10px] min-w-[1.5rem] text-center"
                        >
                          {k}
                        </kbd>
                      ))}
                    </div>
                    <div className="text-sm text-foreground/90 leading-snug">{it.action}</div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="px-6 py-3 border-t border-border text-[10px] text-muted-foreground font-mono uppercase tracking-widest">
          Shortcuts ignored while typing · click background or Esc to close
        </div>
      </div>
    </div>
  );
}
